<template>
  <div
    class="section-header"
    :class="{ 'edit-mode': editMode }"
  >
    <div
      v-if="editMode"
      class="drag-handle"
      title="Drag to reorder"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="6"
          cy="3.5"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="10"
          cy="3.5"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="6"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="10"
          cy="8"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="6"
          cy="12.5"
          r="1.2"
          fill="currentColor"
        />
        <circle
          cx="10"
          cy="12.5"
          r="1.2"
          fill="currentColor"
        />
      </svg>
    </div>
    <div
      v-if="editMode"
      class="section-header-edit"
      @click="focusInput"
    >
      <span
        class="title-input-sizer"
        :data-value="localTitle || 'Section title'"
      >
        <input
          ref="titleInput"
          v-model="localTitle"
          class="section-title-input"
          placeholder="Section title"
          @input="handleTitleChange"
          @blur="handleTitleChange"
          @keyup.enter="handleTitleChange"
          @keyup.esc="cancelEdit"
        >
      </span>
    </div>
    <div
      v-else
      class="section-header-display"
      @click="toggleExpanded"
    >
      <span
        class="expand-arrow"
        :class="{ expanded }"
        aria-hidden="true"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <h2 class="section-title">
        {{ title }}
      </h2>
    </div>
    <div
      v-if="editMode"
      class="header-actions"
    >
      <div
        class="display-mode-toggle"
        title="Toggle display mode"
      >
        <button
          class="toggle-btn"
          :class="{ 'toggle-btn-active': displayMode === 'focused' }"
          @click="handleDisplayModeClick('focused')"
        >
          Focused
        </button>
        <button
          class="toggle-btn"
          :class="{ 'toggle-btn-active': displayMode === 'detailed' }"
          @click="handleDisplayModeClick('detailed')"
        >
          Detailed
        </button>
      </div>
      <div
        v-click-outside="closeDropdown"
        class="overflow-menu-container"
      >
        <button
          class="overflow-btn"
          title="Section options"
          @click="toggleDropdown"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="9"
              cy="4.5"
              r="1.5"
              fill="currentColor"
            />
            <circle
              cx="9"
              cy="9"
              r="1.5"
              fill="currentColor"
            />
            <circle
              cx="9"
              cy="13.5"
              r="1.5"
              fill="currentColor"
            />
          </svg>
        </button>
        <div
          v-if="showDropdown"
          class="overflow-dropdown"
        >
          <button
            class="dropdown-item"
            :disabled="!canMoveUp"
            @click="handleMoveUpClick"
          >
            Move up
          </button>
          <button
            class="dropdown-item"
            :disabled="!canMoveDown"
            @click="handleMoveDownClick"
          >
            Move down
          </button>
          <button
            class="dropdown-item dropdown-item-danger"
            @click="handleDeleteClick"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'New Section'
  },
  expanded: {
    type: Boolean,
    default: true
  },
  displayMode: {
    type: String,
    default: 'detailed',
    validator: (v) => ['focused', 'detailed'].includes(v)
  },
  editMode: {
    type: Boolean,
    default: false
  },
  canMoveUp: {
    type: Boolean,
    default: false
  },
  canMoveDown: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:title', 'update:expanded', 'update:displayMode', 'delete', 'move-up', 'move-down'])

const localTitle = ref(props.title)
const titleInput = ref(null)
const showDropdown = ref(false)

watch(() => props.title, (newTitle) => {
  localTitle.value = newTitle
})

watch(() => props.editMode, (newMode) => {
  // Don't auto-focus when entering edit mode - let user click to edit
  // This prevents all section headers from being highlighted when edit mode is enabled
})

const handleTitleChange = () => {
  const newTitle = localTitle.value.trim() || 'New Section'
  if (newTitle !== props.title) {
    emit('update:title', newTitle)
  }
}

const cancelEdit = () => {
  localTitle.value = props.title
  titleInput.value?.blur()
}

const focusInput = () => {
  if (props.editMode && titleInput.value) {
    titleInput.value.focus()
    titleInput.value.select()
  }
}

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

const closeDropdown = () => {
  showDropdown.value = false
}

const handleDelete = () => {
  if (confirm('Are you sure you want to delete this section?')) {
    emit('delete')
    closeDropdown()
  }
}

const handleMoveUp = () => {
  if (props.canMoveUp) {
    emit('move-up')
  }
}

const handleMoveDown = () => {
  if (props.canMoveDown) {
    emit('move-down')
  }
}

const handleMoveUpClick = () => {
  handleMoveUp()
  closeDropdown()
}

const handleMoveDownClick = () => {
  handleMoveDown()
  closeDropdown()
}

const handleDeleteClick = () => {
  handleDelete()
}

const handleDisplayModeClick = (mode) => {
  emit('update:displayMode', mode)
}

const toggleExpanded = () => {
  emit('update:expanded', !props.expanded)
}

// Click outside directive
const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent)
  }
}
</script>

<style scoped>
.section-header {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 12px 0 0;
  background: transparent;
  border: none;
  box-shadow: none;
  gap: 8px;
}

.section-header.edit-mode {
  padding: 0;
  align-items: center;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  flex-shrink: 0;
  color: #9ca3af;
  cursor: grab;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.drag-handle:hover {
  color: #6b7280;
  background: #f3f4f6;
}

.drag-handle:active {
  cursor: grabbing;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.display-mode-toggle {
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.toggle-btn {
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.toggle-btn-active {
  background: rgba(35, 131, 226, 0.1);
  color: #2383e2;
}

.toggle-btn-active:hover {
  background: rgba(35, 131, 226, 0.15);
}

.overflow-menu-container {
  position: relative;
  flex-shrink: 0;
  z-index: 10001;
}

.overflow-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.overflow-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.overflow-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: none;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.14), 0 3px 6px rgba(0, 0, 0, 0.1);
  min-width: 140px;
  z-index: 10000;
  overflow: hidden;
  padding: 8px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  text-align: left;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover:not(:disabled) {
  background: #f3f4f6;
}

.dropdown-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dropdown-item-danger {
  color: #dc2626;
}

.dropdown-item-danger:hover:not(:disabled) {
  background: #fee;
  color: #dc2626;
}

.dropdown-item-group {
  padding: 8px 16px 4px;
}

.dropdown-item-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.dropdown-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

.dropdown-item-active {
  background: rgba(35, 131, 226, 0.1);
  color: #2383e2;
  font-weight: 500;
}

.dropdown-item-active:hover {
  background: rgba(35, 131, 226, 0.15);
}

.section-header-edit {
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  background-color: rgba(244, 245, 247, 1);
  border: none;
  padding: 8px;
  border-radius: 4px;
}

.title-input-sizer {
  display: inline-grid;
  vertical-align: top;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  grid-template-rows: min-content;
}

.title-input-sizer::after {
  content: attr(data-value) " ";
  visibility: hidden;
  white-space: pre;
  font: inherit;
  grid-area: 1 / 1 / 2 / 2;
}

.section-title-input {
  grid-area: 1 / 1 / 2 / 2;
  width: 100%;
  min-width: 100%;
  height: 1.2em;
  border: none;
  outline: none;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: rgba(51, 51, 51, 1);
  background: transparent;
  padding: 0;
}

.section-title-input::placeholder {
  color: #9ca3af;
}

.section-header-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.expand-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.expand-arrow.expanded {
  transform: rotate(0deg);
}

.expand-arrow:not(.expanded) {
  transform: rotate(-90deg);
}

.section-title {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  color: rgba(51, 51, 51, 1);
  line-height: 1.2;
  user-select: none;
  transition: color 0.2s;
}

.section-title:hover {
  color: rgba(0, 0, 0, 0.87);
}
</style>
